import React from 'react';
import moment from "moment";
import neoLogo from "../../../assets/images/Neo_Logo_A.svg";

// Importações dos domínios para mapeamentos e descrições corretas
import {
    RUNNING_INTENSITY_ZONES,
    RUNNING_SEGMENT_TYPES,
    getRunningLabel,
} from "../../Training/Workouts/domain/running/constants";

import { STRENGTH_EXERCISE_METHODS } from "../../Training/Workouts/domain/strength/constants";
import { WOD_FORMATS, WOD_SCORE_TYPES } from "../../Training/Workouts/domain/wod/constants";

/**
 * ThermalPrintableWorkout
 * Componente oculto na tela que renderiza o treino selecionado
 * otimizado para impressoras térmicas (ex: largura 80mm ou 58mm).
 * Evita o uso de tabelas muito largas e empilha as informações de cada série.
 */
class ThermalPrintableWorkout extends React.Component {

    // Helper para obter zona de esforço da corrida
    getRunningZone(value) {
        return (RUNNING_INTENSITY_ZONES || []).find(zone => zone.value === value);
    }

    // Helper para formatar o alvo de corrida
    formatRunningTarget(segment, paceGroups, clubSessionActive) {
        if (!segment) return "";

        if (segment.targetType === "pace") {
            // Se for treino de clube ativo, mostramos o pace de cada grupo ativo
            if (clubSessionActive && paceGroups && paceGroups.length > 0) {
                const groupPaces = paceGroups.map(group => {
                    const groupKey = group.id || group.value;
                    const customTarget = segment.paceGroupTargetsById?.[groupKey] || {};
                    
                    // Verifica se tem pace customizado para este segmento
                    if (customTarget.targetPaceMin || customTarget.targetPaceMax) {
                        return `${group.label}: ${customTarget.targetPaceMin || "-"}-${customTarget.targetPaceMax || "-"}`;
                    }
                    
                    // Fallback para os paces padrão do grupo com base no tipo de segmento
                    let defaultPace = "-";
                    if (segment.segmentType === "interval") {
                        defaultPace = group.intervalPace || "-";
                    } else if (segment.segmentType === "run" && (group.longRunPace || group.easyPace)) {
                        defaultPace = group.longRunPace || group.easyPace || "-";
                    } else if (segment.segmentType === "walk") {
                        defaultPace = "Caminhar";
                    } else {
                        defaultPace = group.easyPace || "-";
                    }
                    return `${group.label}: ${defaultPace}`;
                });
                return groupPaces.join(" | ");
            }

            if (segment.targetPaceMin || segment.targetPaceMax) {
                return `${segment.targetPaceMin || "-"} a ${segment.targetPaceMax || "-"}/km`;
            }
            return "";
        }

        if (segment.targetType === "heart_rate") return segment.heartRateZone ? `FC: ${segment.heartRateZone}` : "";
        if (segment.targetType === "power") return segment.powerZone ? `Potência: ${segment.powerZone}` : "";
        if (segment.targetType === "cadence") return segment.cadenceTarget ? `Cadência: ${segment.cadenceTarget} RPM` : "";
        if (segment.targetType === "effort") {
            const zone = this.getRunningZone(segment.effortZone);
            return zone ? `${zone.label} (${zone.desc})` : (segment.effortZone || "");
        }
        return "";
    }

    // Helper para formatar a duração do segmento de corrida
    formatRunningDuration(segment) {
        if (!segment) return "";
        const reps = Number(segment.reps || 1);
        const prefix = reps > 1 ? `${reps}x ` : "";

        if (segment.durationType === "time") return `${prefix}${segment.duration || "-"} min`;
        if (segment.durationType === "distance") return `${prefix}${segment.distance || "-"}${segment.distanceUnit || "km"}`;
        if (segment.durationType === "open") return `${prefix}Livre`;
        if (segment.durationType === "lap_button") return `${prefix}Botão Lap`;
        return `${prefix}${segment.duration || segment.distance || "Livre"}`;
    }

    renderRunningBlock(sets, block, workout) {
        const clubSession = workout?.clubSession || {};
        const isClub = !!clubSession.isClubSession;
        const paceGroups = (clubSession.paceGroups || []).filter(g => g.enabled !== false);

        return (sets || []).map((set, idx) => {
            const segmentLabel = set.label || getRunningLabel(RUNNING_SEGMENT_TYPES, set.segmentType);
            const durationText = this.formatRunningDuration(set);
            const targetText = this.formatRunningTarget(set, paceGroups, isClub);

            return (
                <div key={idx} style={{ borderBottom: "1px dashed #000", paddingBottom: "6px", marginBottom: "6px" }}>
                    {/* Linha principal: Duração/Repetições + Tipo */}
                    <div style={{ fontWeight: "bold", fontSize: "14px", display: "flex", justifyContent: "space-between" }}>
                        <span>{idx + 1}. {durationText} {segmentLabel}</span>
                    </div>

                    {/* Alvo de intensidade (Pace, Esforço, FC...) */}
                    {targetText && (
                        <div style={{ fontSize: "12px", marginTop: "2px", color: "#333" }}>
                            <strong>Alvo:</strong> {targetText}
                        </div>
                    )}

                    {/* Recuperação / RestAfter */}
                    {set.restAfter && (
                        <div style={{ fontSize: "12px", marginTop: "1px" }}>
                            <strong>Recup. pós-tiro:</strong> {set.restAfter}
                        </div>
                    )}

                    {/* Terreno */}
                    {set.terrain && set.terrain !== "flat" && (
                        <div style={{ fontSize: "11px", marginTop: "1px", fontStyle: "italic" }}>
                            Terreno: {set.terrain}
                        </div>
                    )}

                    {/* Notas específicas do segmento */}
                    {set.notes && (
                        <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "2px", color: "#555" }}>
                            Obs: {set.notes}
                        </div>
                    )}
                </div>
            );
        });
    }

    renderSwimmingBlock(sets) {
        return (sets || []).map((set, idx) => (
            <div key={idx} style={{ borderBottom: "1px dashed #000", paddingBottom: "6px", marginBottom: "6px" }}>
                {/* Cabeçalho da série de natação: reps x dist estilo */}
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                    {idx + 1}. {set.reps ? `${set.reps}x ` : ""}{set.distance ? `${set.distance}m ` : ""}{set.style || "Livre"}
                </div>
                
                {/* Intensidade e Intervalo */}
                {(set.intensity || set.interval) && (
                    <div style={{ fontSize: "12px", display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
                        <span>{set.intensity && `Intensidade: ${set.intensity}`}</span>
                        <span>{set.interval && `Saída/Intrv: ${set.interval}`}</span>
                    </div>
                )}

                {/* Equipamento */}
                {set.equipment && set.equipment !== "Sem material" && (
                    <div style={{ fontSize: "11px", marginTop: "2px" }}>
                        <strong>Material:</strong> {set.equipment}
                    </div>
                )}

                {/* Foco Técnico */}
                {set.technicalFocus && (
                    <div style={{ fontSize: "11px", marginTop: "1px" }}>
                        <strong>Foco Técnico:</strong> {set.technicalFocus}
                    </div>
                )}

                {/* Descrição / Observações */}
                {set.description && (
                    <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "2px" }}>
                        Obs: {set.description}
                    </div>
                )}
            </div>
        ));
    }

    renderStrengthBlock(sets) {
        return (sets || []).map((set, idx) => {
            const methodObj = (STRENGTH_EXERCISE_METHODS || []).find(m => m.value === set.strengthMethod);
            const methodName = methodObj ? methodObj.label : set.strengthMethod;
            const hasDetails = set.rest || set.tempo || set.muscleGroup || set.equipment;

            return (
                <div key={idx} style={{ borderBottom: "1px dashed #000", paddingBottom: "6px", marginBottom: "6px" }}>
                    {/* Exercício */}
                    <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {idx + 1}. {set.exercise || set.name || set.movement || "Exercício"}
                    </div>

                    {/* Séries x Repetições e Carga */}
                    {(set.sets || set.reps || set.load) && (
                        <div style={{ fontSize: "12px", display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "2px", fontWeight: "500" }}>
                            {set.sets && <span>{set.sets} séries</span>}
                            {set.reps && <span>x {set.reps} reps</span>}
                            {set.load && <span style={{ backgroundColor: "#eaeaea", padding: "0 4px" }}>@ {set.load}kg</span>}
                        </div>
                    )}

                    {/* Detalhes de execução */}
                    {hasDetails && (
                        <div style={{ fontSize: "11px", marginTop: "2px", display: "flex", flexWrap: "wrap", gap: "8px", color: "#444" }}>
                            {set.rest && <span>Descanso: {set.rest}</span>}
                            {set.tempo && <span>Cadência: {set.tempo}</span>}
                            {set.muscleGroup && <span>Grupo: {set.muscleGroup}</span>}
                            {set.equipment && <span>Equip: {set.equipment}</span>}
                        </div>
                    )}

                    {/* Método especial (dropset, pirâmide, etc.) */}
                    {set.strengthMethod && set.strengthMethod !== "straight" && (
                        <div style={{ fontSize: "11px", marginTop: "2px", fontWeight: "bold", color: "#d9534f" }}>
                            Método: {methodName}
                        </div>
                    )}

                    {/* Observações do exercício */}
                    {set.description && (
                        <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "2px", color: "#555" }}>
                            Obs: {set.description}
                        </div>
                    )}

                    {/* Instruções do exercício */}
                    {set.instructions && (
                        <div style={{ fontSize: "10px", marginTop: "2px", padding: "2px 4px", borderLeft: "2px solid #555", color: "#666" }}>
                            Instruções: {set.instructions}
                        </div>
                    )}
                </div>
            );
        });
    }

    renderWodBlock(sets, block) {
        return (sets || []).map((set, idx) => {
            const hasMetas = set.distance || set.calories || set.time || set.height;
            const hasScaling = set.scaling && (set.scaling.rx || set.scaling.intermediate || set.scaling.beginner);

            return (
                <div key={idx} style={{ borderBottom: "1px dashed #000", paddingBottom: "6px", marginBottom: "6px" }}>
                    {/* Movimento do WOD */}
                    <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {idx + 1}. {set.movement || set.exercise || set.name || "Movimento"}
                    </div>

                    {/* Reps e Carga */}
                    {(set.reps || set.load) && (
                        <div style={{ fontSize: "12px", display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "2px" }}>
                            {set.reps && <span><strong>Reps:</strong> {set.reps} {set.unit || ""}</span>}
                            {set.load && <span style={{ backgroundColor: "#eaeaea", padding: "0 4px" }}><strong>Carga:</strong> {set.load}</span>}
                        </div>
                    )}

                    {/* Outras metas de WOD (Calorias, distância, tempo, altura) */}
                    {hasMetas && (
                        <div style={{ fontSize: "11px", display: "flex", flexWrap: "wrap", gap: "6px", color: "#444", marginTop: "2px" }}>
                            {set.distance && <span>Dist: {set.distance}</span>}
                            {set.calories && <span>Cal: {set.calories}</span>}
                            {set.time && <span>Tempo: {set.time}</span>}
                            {set.height && <span>Alt: {set.height}</span>}
                        </div>
                    )}

                    {/* Scaling / Adaptações do movimento */}
                    {hasScaling && (
                        <div style={{ fontSize: "10px", marginTop: "2px", color: "#444", borderLeft: "2px solid #aaa", paddingLeft: "4px" }}>
                            {set.scaling.rx && <div><strong>RX:</strong> {set.scaling.rx}</div>}
                            {set.scaling.intermediate && <div><strong>Interm:</strong> {set.scaling.intermediate}</div>}
                            {set.scaling.beginner && <div><strong>Inic:</strong> {set.scaling.beginner}</div>}
                        </div>
                    )}

                    {/* Notas específicas do movimento */}
                    {set.notes && (
                        <div style={{ fontSize: "11px", fontStyle: "italic", marginTop: "2px", color: "#555" }}>
                            Obs: {set.notes}
                        </div>
                    )}
                </div>
            );
        });
    }

    renderBlock(workoutType, block, workout) {
        const sets = block.sets;
        if (!sets || sets.length === 0) {
            return <div style={{ fontSize: "12px", fontStyle: "italic", textAlign: "center", margin: "10px 0" }}>Nenhum exercício neste bloco.</div>;
        }

        switch (workoutType) {
            case "swimming": return this.renderSwimmingBlock(sets);
            case "strength": return this.renderStrengthBlock(sets);
            case "running": return this.renderRunningBlock(sets, block, workout);
            case "wod": return this.renderWodBlock(sets, block);
            default: return this.renderSwimmingBlock(sets);
        }
    }

    render() {
        const { workout, tenantName, memberName } = this.props;

        if (!workout) return null;

        const workoutType = workout.workoutType || "strength";
        const clubSession = workout.clubSession || {};
        const isClub = !!clubSession.isClubSession;
        const paceGroups = (clubSession.paceGroups || []).filter(g => g.enabled !== false);
        const cyclePlan = workout.cyclePlan || {};
        const isCycleActive = !!cyclePlan.enabled;

        return (
            <div style={{ 
                padding: "10px", 
                fontFamily: "'Courier New', Courier, monospace", // Fonte ideal para impressoras térmicas
                color: "#000", 
                backgroundColor: "#fff",
                width: "100%",
                maxWidth: "302px", // Largura aproximada de 80mm
                margin: "0 auto"
            }}>
                {/* CABEÇALHO DA IMPRESSÃO TÉRMICA */}
                <div style={{ borderBottom: "2px dashed #000", paddingBottom: "10px", marginBottom: "10px" }}>
                    <h3 style={{ textAlign: "center", margin: "0 0 12px 0", fontSize: "18px", fontWeight: "bold", textTransform: "uppercase" }}>
                        {workout?.title || workout?.name || "Treino"}
                    </h3>
                    
                    {/* Info Alinhada à Esquerda */}
                    <div style={{ fontSize: "12px", marginTop: "5px", textAlign: "left" }}>
                        <p style={{ margin: "2px 0" }}><strong>Academia:</strong> {tenantName || "NeoApp"}</p>
                        <p style={{ margin: "2px 0" }}><strong>Aluno:</strong> {memberName || "Aluno(a)"}</p>
                        {workout?.teacherName && <p style={{ margin: "2px 0" }}><strong>Professor:</strong> {workout.teacherName}</p>}
                        <p style={{ margin: "2px 0" }}><strong>Modalidade:</strong> {
                            workoutType === "swimming" ? "Natação" :
                            workoutType === "strength" ? "Musculação" :
                            workoutType === "running" ? "Corrida" :
                            workoutType === "wod" ? "WOD" : workoutType
                        }</p>
                    </div>
                </div>

                {/* LOGÍSTICA DO TREINO DE CLUBE (APENAS PARA CORRIDA COM CLUBE ATIVO) */}
                {workoutType === "running" && isClub && (
                    <div style={{ style: "solid", marginBottom: "15px", padding: "6px", border: "1px dashed #000", fontSize: "11px" }}>
                        <div style={{ fontWeight: "bold", textAlign: "center", marginBottom: "6px", textTransform: "uppercase", fontSize: "12px" }}>
                            === DETALHES TREINO CLUBE ===
                        </div>
                        {clubSession.teamName && <p style={{ margin: "2px 0" }}><strong>Turma:</strong> {clubSession.teamName}</p>}
                        {clubSession.coachName && <p style={{ margin: "2px 0" }}><strong>Prof. Responsável:</strong> {clubSession.coachName}</p>}
                        {clubSession.startTime && <p style={{ margin: "2px 0" }}><strong>Horário:</strong> {clubSession.startTime}</p>}
                        {clubSession.meetingPoint && <p style={{ margin: "2px 0" }}><strong>Encontro:</strong> {clubSession.meetingPoint}</p>}
                        {clubSession.routeName && <p style={{ margin: "2px 0" }}><strong>Rota:</strong> {clubSession.routeName}</p>}
                        {clubSession.safetyNotes && (
                            <p style={{ margin: "4px 0 0 0", borderTop: "1px dotted #000", paddingTop: "4px" }}>
                                <strong>Segurança:</strong> {clubSession.safetyNotes}
                            </p>
                        )}
                    </div>
                )}

                {/* REFERÊNCIA DE PACES DOS GRUPOS (APENAS CORRIDA COM CLUBE E GRUPOS ATIVOS) */}
                {workoutType === "running" && isClub && paceGroups.length > 0 && (
                    <div style={{ marginBottom: "15px", padding: "6px", border: "1px dashed #000", fontSize: "11px" }}>
                        <div style={{ fontWeight: "bold", textAlign: "center", marginBottom: "6px", textTransform: "uppercase", fontSize: "12px" }}>
                            === PACES DOS GRUPOS ===
                        </div>
                        {paceGroups.map((group, gIdx) => (
                            <div key={group.id || group.value || gIdx} style={{ marginBottom: gIdx < paceGroups.length - 1 ? "6px" : "0", paddingBottom: gIdx < paceGroups.length - 1 ? "4px" : "0", borderBottom: gIdx < paceGroups.length - 1 ? "1px dotted #ccc" : "none" }}>
                                <strong>{group.label} ({group.desc || "Grupo"})</strong>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 6px", marginTop: "2px" }}>
                                    {group.easyPace && <span>Leve: {group.easyPace}</span>}
                                    {group.thresholdPace && <span>Limiar: {group.thresholdPace}</span>}
                                    {group.intervalPace && <span>Tiros: {group.intervalPace}</span>}
                                    {group.longRunPace && <span>Longão: {group.longRunPace}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* DETALHES DE CICLO E PROVA */}
                {isCycleActive && (
                    <div style={{ marginBottom: "15px", padding: "6px", border: "1px dashed #000", fontSize: "11px" }}>
                        <div style={{ fontWeight: "bold", textAlign: "center", marginBottom: "6px", textTransform: "uppercase", fontSize: "12px" }}>
                            === PLANEJAMENTO DE CICLO ===
                        </div>
                        {cyclePlan.raceName && <p style={{ margin: "2px 0" }}><strong>Meta/Prova:</strong> {cyclePlan.raceName}</p>}
                        {(cyclePlan.raceDate || cyclePlan.raceDistance) && (
                            <p style={{ margin: "2px 0" }}>
                                <strong>Info Prova:</strong> {cyclePlan.raceDistance ? `${cyclePlan.raceDistance} ` : ""}{cyclePlan.raceDate ? `em ${moment(cyclePlan.raceDate).format("DD/MM/YYYY")}` : ""}
                            </p>
                        )}
                        {(cyclePlan.weekNumber || cyclePlan.totalWeeks) && (
                            <p style={{ margin: "2px 0" }}>
                                <strong>Semana:</strong> {cyclePlan.weekNumber || "-"}{cyclePlan.totalWeeks ? ` de ${cyclePlan.totalWeeks}` : ""}
                            </p>
                        )}
                        {cyclePlan.phase && <p style={{ margin: "2px 0" }}><strong>Fase:</strong> {cyclePlan.phase.toUpperCase()}</p>}
                        {cyclePlan.plannedWeeklyKm && <p style={{ margin: "2px 0" }}><strong>Volume Semanal:</strong> {cyclePlan.plannedWeeklyKm} km/sem</p>}
                    </div>
                )}

                {/* DESCRIÇÃO DO TREINO */}
                {workout?.description && (
                    <div style={{ marginBottom: "15px", paddingBottom: "10px", borderBottom: "2px dashed #000" }}>
                        <strong style={{ fontSize: "12px" }}>Objetivos:</strong>
                        <p style={{ margin: "4px 0 0 0", fontSize: "12px", whiteSpace: "pre-wrap" }}>
                            {workout.description}
                        </p>
                    </div>
                )}

                {/* BLOCOS DE TREINO */}
                {(workout.blocks || []).map((block, bIndex) => {
                    const isWodBlock = workoutType === "wod";
                    const hasWodSpecs = isWodBlock && (block.format || block.timeCap || block.stimulus || block.scalingNotes);

                    return (
                        <div key={bIndex} style={{ marginBottom: "15px" }}>
                            {/* Nome do Bloco */}
                            <div style={{ 
                                backgroundColor: "#000", 
                                color: "#fff", 
                                padding: "4px", 
                                textAlign: "center", 
                                fontWeight: "bold", 
                                fontSize: "14px",
                                marginBottom: "8px",
                                textTransform: "uppercase"
                            }}>
                                {block.name || `Bloco ${bIndex + 1}`}
                            </div>

                            {/* Informações de repetição do bloco de corrida */}
                            {workoutType === "running" && block.repeat && block.repeat > 1 && (
                                <div style={{ fontSize: "12px", fontWeight: "bold", textAlign: "center", marginBottom: "8px", borderBottom: "1px dotted #000", paddingBottom: "4px" }}>
                                    REPETIR {block.repeat}x {block.restBetweenRepeats ? `(Descanso: ${block.restBetweenRepeats})` : ""}
                                </div>
                            )}

                            {/* Especificações de Bloco WOD */}
                            {hasWodSpecs && (
                                <div style={{ marginBottom: "8px", padding: "4px", border: "1px dotted #000", fontSize: "11px" }}>
                                    {block.format && (
                                        <p style={{ margin: "2px 0" }}>
                                            <strong>Formato:</strong> {
                                                (WOD_FORMATS || []).find(f => f.value === block.format)?.label?.toUpperCase() || block.format.toUpperCase()
                                            }
                                        </p>
                                    )}
                                    {block.timeCap && <p style={{ margin: "2px 0" }}><strong>Time Cap:</strong> {block.timeCap}</p>}
                                    {block.scoreType && (
                                        <p style={{ margin: "2px 0" }}>
                                            <strong>Placar:</strong> {
                                                (WOD_SCORE_TYPES || []).find(s => s.value === block.scoreType)?.label || block.scoreType
                                            }
                                        </p>
                                    )}
                                    {block.stimulus && <p style={{ margin: "2px 0" }}><strong>Estímulo:</strong> {block.stimulus}</p>}
                                    {block.scalingNotes && <p style={{ margin: "4px 0 0 0", borderTop: "1px dashed #ccc", paddingTop: "2px" }}><strong>Adaptações:</strong> {block.scalingNotes}</p>}
                                </div>
                            )}

                            {block.description && (
                                <p style={{ margin: "0 0 8px 0", fontSize: "11px", fontStyle: "italic", color: "#555" }}>
                                    {block.description}
                                </p>
                            )}
                            
                            {/* Renderizador das séries/exercícios do bloco */}
                            {this.renderBlock(workoutType, block, workout)}
                        </div>
                    );
                })}

                {/* RODAPÉ */}
                <div style={{ marginTop: "20px", paddingTop: "10px", borderTop: "2px dashed #000", textAlign: "right" }}>
                    <p style={{ margin: "0 0 5px 0", fontSize: "10px" }}>
                        Impresso em {moment().format("DD/MM/YYYY HH:mm")}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: "5px" }}>
                        <span style={{ fontSize: "10px", fontWeight: "bold", marginRight: "4px" }}>POWERED BY</span>
                        <img src={neoLogo} alt="NeoApp" style={{ height: "12px", filter: "grayscale(100%) contrast(200%)" }} />
                    </div>
                    {/* Espaço em branco no final para o corte de papel térmico */}
                    <div style={{ height: "30px" }}></div>
                </div>
            </div>
        );
    }
}

export default ThermalPrintableWorkout;
